import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, MailOpen, XCircle } from "lucide-react";
import { api, errMsg, PENDING_INVITE_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PublicLayout, HouseQ } from "@/components/brand/Brand";
import { Loader } from "@/components/common";

export default function InvitePage() {
  const { token } = useParams();
  const { session, refresh } = useAuth();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`).then(({ data }) => setInvite(data)).catch((e) => setError(errMsg(e)));
  }, [token]);

  useEffect(() => {
    if (session && session.type === "adult") localStorage.removeItem(PENDING_INVITE_KEY);
  }, [session]);

  const remember = () => localStorage.setItem(PENDING_INVITE_KEY, token);

  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`);
      await refresh();
      if (data.already_member) {
        setNotice("You're already in this hub");
        setTimeout(() => nav(`/h/${data.organization_id}/home`, { replace: true }), 1400);
      } else {
        nav(`/h/${data.organization_id}/home`, { replace: true });
      }
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  let body;
  if (error) {
    body = (
      <div className="text-center" data-testid="invite-invalid">
        <XCircle className="mx-auto h-10 w-10 text-red-400" />
        <p className="mt-4 font-display text-xl font-bold text-q-text" data-testid="invite-error">{error}</p>
        <Link to="/" className="q-btn q-btn-secondary mt-6">Back to home</Link>
      </div>
    );
  } else if (!invite || session === null) {
    body = <Loader />;
  } else {
    body = (
      <div className="text-center" data-testid="invite-valid">
        <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full" style={{ background: "rgba(var(--glow-family),0.14)" }}>
          <MailOpen className="h-6 w-6" style={{ color: "rgb(var(--glow-family))" }} />
        </span>
        <p className="mt-5 text-sm text-q-muted">{invite.inviter_name} invited you to join</p>
        <h1 className="mt-1 font-display text-3xl font-extrabold text-q-text" data-testid="invite-hub-name">{invite.hub_name}</h1>
        <p className="mt-2 text-sm text-q-muted">as <span className="font-semibold text-q-text" data-testid="invite-role">{invite.role_label}</span></p>
        {notice && <p className="mt-5 rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-sm font-semibold text-cyan-200" data-testid="invite-notice">{notice}</p>}
        {session && session.type === "adult" ? (
          <>
            <button onClick={join} disabled={busy || !!notice} className="q-btn q-btn-primary mt-7 w-full" data-testid="invite-join-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Join
            </button>
            <p className="mt-3 text-xs text-q-faint">Signed in as {session.user.email}</p>
          </>
        ) : (
          <div className="mt-7 grid gap-3">
            <Link to="/signup" onClick={remember} className="q-btn q-btn-primary" data-testid="invite-signup-btn">Create an account to join</Link>
            <Link to="/signin" onClick={remember} className="q-btn q-btn-secondary" data-testid="invite-signin-btn">I already have an account</Link>
          </div>
        )}
      </div>
    );
  }

  return (
    <PublicLayout>
      <div className="w-full max-w-md q-rise" data-testid="invite-page">
        <div className="mb-6 flex justify-center"><HouseQ size={48} /></div>
        <div className="q-card">{body}</div>
      </div>
    </PublicLayout>
  );
}
